import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplates } from '../../contexts/TemplateContext';

const CreateTemplate = () => {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const { addTemplate } = useTemplates();
  const [templateData, setTemplateData] = useState({
    name: '',
    category: 'Marketing',
    content: '',
  });
  const [charCount, setCharCount] = useState(0);
  const [smsCount, setSmsCount] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = ['Marketing', 'Notifications', 'Reminders', 'Custom'];
  
  const variables = [
    { name: 'Customer Name', value: '{name}' },
    { name: 'Current Date', value: '{date}' },
    { name: 'Current Time', value: '{time}' },
    { name: 'Order Number', value: '{order_number}' },
    { name: 'Appointment Date', value: '{appointment_date}' },
    { name: 'Appointment Time', value: '{appointment_time}' },
    { name: 'Company Name', value: '{company_name}' },
    { name: 'Customer Email', value: '{email}' },
    { name: 'Customer Phone', value: '{phone}' },
    { name: 'Custom Variable', value: '{custom_variable}' },
  ];

  // Update character count whenever content changes
  useEffect(() => {
    setCharCount(templateData.content.length);
    
    // Calculate SMS count (assuming 160 characters per SMS)
    // This is a simplified calculation - actual SMS counting might be more complex
    const smsCount = Math.ceil(templateData.content.length / 160);
    setSmsCount(smsCount);
  }, [templateData.content]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Validate template data
      if (!templateData.name.trim()) {
        throw new Error('Template name is required');
      }
      
      if (!templateData.content.trim()) {
        throw new Error('Template content is required');
      }
      
      // Add the template to the context
      addTemplate({
        name: templateData.name,
        category: templateData.category,
        content: templateData.content
      });
      
      // Navigate back to templates page
      navigate('/superadmin/templates');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while creating the template');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTemplateData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const insertVariable = (variable: string) => {
    if (contentRef.current) {
      const textarea = contentRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateData.content;
      const before = text.substring(0, start);
      const after = text.substring(end);
      
      setTemplateData(prev => ({
        ...prev,
        content: before + variable + after
      }));
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-medium mb-6">Create New Template</h1>

      <div className="bg-white rounded-lg shadow-md p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Template Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Template Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={templateData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              placeholder="Enter template name"
              required
            />
          </div>

          {/* Category Selection */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              id="category"
              name="category"
              value={templateData.category}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              required
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Template Content with Variable Insertion */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                Template Content
              </label>
              <div className="relative">
                <select 
                  className="text-sm px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  onChange={(e) => {
                    if (e.target.value) {
                      insertVariable(e.target.value);
                      e.target.value = ''; // Reset the dropdown
                    }
                  }}
                >
                  <option value="">Insert Variable</option>
                  {variables.map((variable) => (
                    <option key={variable.value} value={variable.value}>
                      {variable.name} ({variable.value})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <textarea
              id="content"
              name="content"
              ref={contentRef}
              value={templateData.content}
              onChange={handleChange}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              placeholder="Enter your template content. Use variables for dynamic content."
              required
            />
            
            {/* Character Counter */}
            <div className="mt-2 flex justify-between items-center text-sm">
              <div className="flex items-center space-x-2">
                <span className="text-gray-600">Characters:</span>
                <span className={`font-medium ${charCount > 160 ? 'text-red-500' : 'text-gray-900'}`}>
                  {charCount}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-gray-600">SMS Count:</span>
                <span className={`font-medium ${smsCount > 1 ? 'text-amber-500' : 'text-gray-900'}`}>
                  {smsCount}
                </span>
              </div>
            </div>
          </div>

          {/* Variable Help Text */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Available Variables</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {variables.map((variable) => (
                <div key={variable.value} className="flex items-center">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm mr-2">{variable.value}</code>
                  <span className="text-sm text-gray-600">{variable.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => navigate('/superadmin/templates')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTemplate; 